/**
 * Saved monthly reports in Hugin's answer (#287): a card per saved file the server named in this turn's tool
 * answers, never from what the model wrote. The file is the export log's own row (GET /admin/pay-files,
 * GET /admin/pay-month, services/payMonth.js), so the card names that exact version: its id, month, format,
 * who saved it and when. Opening it is navigation to Rapporter's saved list on that file; nothing is saved,
 * approved or sent from here. The download is the stored bytes by id, checked by the server with today's
 * access, not with the access the turn had.
 */
import { ApiError, saveFile } from '@/api/client';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { when } from '@/pages/lonn/Month';
import { FILE_LABEL, type SavedFile } from '@/pages/lonn/Saved';
import { useTenant } from '@/shell/tenant';
import { monthLabel } from '@/ui/format';
import { useAuiState } from '@assistant-ui/react';
import React, { useRef, useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';

export interface ReportFile {
  id: number;
  /** YYYY-MM */
  month: string;
  format: SavedFile['format'];
  file_name: string;
  saved_at: string;
  by: string | null;
  lines: number;
  scope: { stores: number; people: number | null } | null;
  /** the export log's short checksum (pay-month only) */
  revision: string | null;
  /** false: the hours changed after this file was saved (pay-month only); null not known */
  same_as_now: boolean | null;
}

/** What one turn's tool answers say about saved reports. */
export type ReportRef =
  | { kind: 'file'; file: ReportFile }
  | { kind: 'none'; month: string | null }
  | { kind: 'no_input'; month: string }
  | { kind: 'changed'; month: string; people: { id: number; name: string }[] }
  | { kind: 'denied' }
  | { kind: 'failed' };

type Part = {
  type?: string;
  toolName?: string;
  args?: { path?: unknown };
  input?: { path?: unknown };
  result?: unknown;
  output?: unknown;
};

const YM = /^\d{4}-\d{2}$/;
const DAY = /^\d{4}-\d{2}-\d{2}/;

function answerOf(output: unknown): { body: Record<string, unknown> | null; error: boolean } {
  const o = output as { isError?: boolean; content?: { text?: string }[] } | string | null | undefined;
  const text = typeof o === 'string' ? o : (o?.content?.map((c) => c.text ?? '').join('') ?? '');
  const error = typeof o === 'object' && o?.isError === true;
  try {
    const v = JSON.parse(text) as unknown;
    return { body: v && typeof v === 'object' ? (v as Record<string, unknown>) : null, error };
  } catch {
    return { body: null, error };
  }
}

/** One export-log row, checked field by field; anything off is left out rather than guessed. */
function fileOf(r: Record<string, unknown>, month: string | null): ReportFile | null {
  const id = Number(r.id);
  const format = r.format as SavedFile['format'];
  const m =
    month ??
    (typeof r.period_start === 'string' && DAY.test(r.period_start) ? r.period_start.slice(0, 7) : null);
  if (!Number.isInteger(id) || id <= 0 || !(format in FILE_LABEL) || !m || !YM.test(m)) return null;
  if (typeof r.exported_at !== 'string' || !Number.isFinite(Number(r.lines))) return null;
  const s = r.scope as { stores?: unknown; people?: unknown } | null | undefined;
  return {
    id,
    month: m,
    format,
    file_name: typeof r.file_name === 'string' && r.file_name ? r.file_name : `${FILE_LABEL[format]} ${m}`,
    saved_at: r.exported_at,
    by: typeof r.exported_by_name === 'string' ? r.exported_by_name : null,
    lines: Number(r.lines),
    scope:
      s && Number.isInteger(s.stores)
        ? { stores: Number(s.stores), people: Number.isInteger(s.people) ? Number(s.people) : null }
        : null,
    revision: typeof r.revision === 'string' ? r.revision : null,
    same_as_now: typeof r.same_as_now === 'boolean' ? r.same_as_now : null,
  };
}

const toolOf = (p: Part) => p.toolName ?? (p.type?.startsWith('tool-') ? p.type.slice(5) : '');
const pathOf = (p: Part) => String(p.args?.path ?? p.input?.path ?? '').replace(/^\//, '');

/**
 * The report references in one message's parts. Only three tool answers count: api_read on
 * /admin/pay-files or /admin/pay-month, and read_month_close for a month with no approved hours.
 */
export function reportRefsIn(parts: readonly unknown[]): ReportRef[] {
  const out: ReportRef[] = [];
  const seen = new Set<number>();
  const add = (f: ReportFile | null) => {
    if (f && !seen.has(f.id)) {
      seen.add(f.id);
      out.push({ kind: 'file', file: f });
    }
  };
  for (const raw of parts) {
    const p = (raw ?? {}) as Part;
    const tool = toolOf(p);
    const output = p.result ?? p.output;
    if (output === undefined) continue;
    const { body, error } = answerOf(output);
    if (tool === 'read_month_close') {
      const counts = body?.counts as { approved?: unknown } | undefined;
      if (!error && typeof body?.month === 'string' && YM.test(body.month) && counts?.approved === 0)
        out.push({ kind: 'no_input', month: body.month });
      continue;
    }
    if (tool !== 'api_read') continue;
    const path = pathOf(p);
    const files = /^admin\/pay-files(\?|$)/.test(path);
    const month = /^admin\/pay-month\?/.test(path);
    if (!files && !month) continue;
    if (error || body?.error) {
      out.push({ kind: Number(body?.status) === 403 ? 'denied' : 'failed' });
      continue;
    }
    const answer = body?.answer as Record<string, unknown> | undefined;
    if (!answer) continue;
    if (files && Array.isArray(answer.files)) {
      const before = seen.size;
      for (const r of answer.files) add(fileOf(r as Record<string, unknown>, null));
      if (seen.size === before && answer.files.length === 0) out.push({ kind: 'none', month: null });
    } else if (month && Array.isArray(answer.exports)) {
      const y = Number(answer.year);
      const mo = Number(answer.month);
      if (!Number.isInteger(y) || !Number.isInteger(mo) || mo < 1 || mo > 12) continue;
      const ym = `${y}-${String(mo).padStart(2, '0')}`;
      const before = seen.size;
      for (const r of answer.exports) add(fileOf(r as Record<string, unknown>, ym));
      if (seen.size === before) out.push({ kind: 'none', month: ym });
      const people = Array.isArray(answer.changed_people)
        ? (answer.changed_people as { id?: unknown; name?: unknown }[])
            .filter((x) => Number.isInteger(x.id) && typeof x.name === 'string')
            .map((x) => ({ id: Number(x.id), name: String(x.name) }))
        : [];
      if (people.length) out.push({ kind: 'changed', month: ym, people });
    }
  }
  return out;
}

/** Rapporter on the saved list, on that file and its month; the page's own parameters (Lonn.tsx). */
export const openPath = (f: Pick<ReportFile, 'id' | 'month'>) =>
  `/lonn?mnd=${f.month}&vis=lagrede&fil=${f.id}`;

function Go({ to, children, primary }: { to: string; children: React.ReactNode; primary?: boolean }) {
  const inRouter = useInRouterContext();
  const cls = primary ? 'pc-send pc-rc-go' : 'pc-ghost pc-rc-go';
  return inRouter ? (
    <Link className={cls} to={to}>
      {children}
    </Link>
  ) : (
    <a className={cls} href={to}>
      {children}
    </a>
  );
}

function FileCard({ f }: { f: ReportFile }) {
  const can = useCan();
  const company = useTenant().tenant?.name ?? '';
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'missing' | 'failed'>('idle');
  const busy = useRef(false);
  const take = async () => {
    if (busy.current) return;
    busy.current = true;
    setState('busy');
    try {
      await saveFile(`/admin/pay-files/${f.id}`, f.file_name);
      setState('done');
    } catch (e) {
      setState(e instanceof ApiError && (e.status === 404 || e.status === 403) ? 'missing' : 'failed');
    } finally {
      busy.current = false;
    }
  };
  const title = `${monthLabel(f.month)}, ${T(FILE_LABEL[f.format])}`;
  const scope = f.scope
    ? [
        f.scope.stores === 1 ? T('1 bedrift') : T('{n} bedrifter', { n: f.scope.stores }),
        f.scope.people != null ? T('{n} valgte personer', { n: f.scope.people }) : null,
      ]
        .filter(Boolean)
        .join(', ')
    : T('hele måneden for {company}', { company });
  return (
    <article className="pc-rc" aria-label={title} data-testid="report-card">
      <span className="pc-rc-kicker">{T('Lagret rapport, ID {id}', { id: f.id })}</span>
      <b className="pc-rc-title">{title}</b>
      <span className="pc-rc-meta">
        {[
          f.by ? T('Lagret {when}, {who}', { when: when(f.saved_at), who: f.by }) : when(f.saved_at),
          f.revision ? T('versjon {v}', { v: f.revision }) : null,
        ]
          .filter(Boolean)
          .join(', ')}
      </span>
      <span className="pc-rc-meta">
        {T('Grunnlag: godkjente timer, {scope}, {n} linjer.', { scope, n: f.lines })}
      </span>
      {f.same_as_now === false && (
        <p className="pc-rc-note pc-rc-warn">
          <span aria-hidden="true">! </span>
          {T(
            'Timene er endret etter at filen ble lagret. Filen er som den var; en ny fil lages i Rapporter.',
          )}
        </p>
      )}
      <div className="pc-card-buttons pc-rc-actions">
        <Go to={openPath(f)} primary>
          {T('Åpne rapporten')}
        </Go>
        {can('timerloenn.sende') && (
          <button
            type="button"
            className="pc-ghost"
            onClick={take}
            disabled={state === 'busy'}
            aria-label={`${T('Last ned originalen')} ${f.file_name}`}
          >
            {state === 'busy' ? T('Henter …') : T('Last ned originalen')}
          </button>
        )}
      </div>
      <output className="pc-rc-status">
        {state === 'done'
          ? T('Filen er lastet ned slik den ble lagret.')
          : state === 'missing'
            ? T('Filen finnes ikke lenger, eller du har ikke tilgang til den nå.')
            : state === 'failed'
              ? T('Filen kunne ikke hentes. Prøv igjen, eller åpne rapporten.')
              : ''}
      </output>
    </article>
  );
}

/** A month with no saved file, no approved hours, a refusal or a failure: what is true, and where to go. */
function StateCard({ r }: { r: Exclude<ReportRef, { kind: 'file' }> }) {
  if (r.kind === 'changed')
    return (
      <div className="pc-rc pc-rc-plain" data-testid="report-changed">
        <span className="pc-rc-meta">
          {T('Endret etter siste lagrede fil for {month}:', { month: monthLabel(r.month) })}
        </span>
        <ul className="pc-rc-refs">
          {r.people.slice(0, 5).map((p) => (
            <li key={p.id}>
              <Go to={`/lonn?mnd=${r.month}&ansatt=${p.id}`}>{p.name}</Go>
            </li>
          ))}
        </ul>
        {r.people.length > 5 && (
          <span className="pc-rc-meta">{T('og {n} til i Rapporter.', { n: r.people.length - 5 })}</span>
        )}
      </div>
    );
  const [text, to, button] =
    r.kind === 'none'
      ? [
          r.month
            ? T('Ingen lagret rapport for {month} ennå. En fil lagres når den lastes ned i Rapporter.', {
                month: monthLabel(r.month),
              })
            : T('Ingen lagrede rapporter ennå. En fil lagres når den lastes ned i Rapporter.'),
          r.month ? `/lonn?mnd=${r.month}&vis=eksport` : '/lonn?vis=eksport',
          T('Lag rapporten i Rapporter'),
        ]
      : r.kind === 'no_input'
        ? [
            T('Ingen godkjente timer for {month} ennå, så det er ingen rapport å lage.', {
              month: monthLabel(r.month),
            }),
            `/lonn?mnd=${r.month}&vis=kontroll`,
            T('Se hva som mangler'),
          ]
        : r.kind === 'denied'
          ? [T('Du har ikke tilgang til lagrede rapporter. Spør administratoren om tilgang.'), null, null]
          : [
              T('De lagrede rapportene kunne ikke hentes nå. Ingenting er endret.'),
              '/lonn?vis=lagrede',
              T('Åpne lagrede rapporter'),
            ];
  return (
    <div className={`pc-rc pc-rc-plain pc-rc-${r.kind}`} data-testid={`report-${r.kind}`}>
      <span className="pc-rc-meta">
        {r.kind === 'failed' || r.kind === 'denied' ? <span aria-hidden="true">! </span> : null}
        {text}
      </span>
      {to && button && (
        <div className="pc-rc-actions">
          <Go to={to}>{button}</Go>
        </div>
      )}
    </div>
  );
}

/** At most three files under one answer; the rest are one link away. */
const SHOWN = 3;

export function ReportCards() {
  const parts = useAuiState((s) => s.message.parts) as readonly unknown[];
  const refs = React.useMemo(() => reportRefsIn(parts || []), [parts]);
  if (!refs.length) return null;
  const files = refs
    .flatMap((r) => (r.kind === 'file' ? [r.file] : []))
    .sort((a, b) => b.saved_at.localeCompare(a.saved_at) || b.id - a.id);
  const states = refs.filter((r): r is Exclude<ReportRef, { kind: 'file' }> => r.kind !== 'file');
  return (
    <div className="pc-rcs" data-testid="report-cards">
      {files.slice(0, SHOWN).map((f) => (
        <FileCard key={f.id} f={f} />
      ))}
      {files.length > SHOWN && (
        <Go to="/lonn?vis=lagrede">{T('Se alle {n} lagrede rapporter', { n: files.length })}</Go>
      )}
      {states.map((r, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list from one stored answer, never reordered
        <StateCard key={i} r={r} />
      ))}
    </div>
  );
}
